/*
 * <copyright company="Argenbright Innovations Lab">
 *        copyright (c) Argenbright Innovations Lab, an Argenbright Holdings Company.  All rights reserved.
 * </copyright>
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useForm, useWatch } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import {
  AlertColor,
  Box,
  Button,
  CircularProgress,
  debounce,
  IconButton,
  Select,
  SelectChangeEvent,
  Stack,
  Typography,
} from '@mui/material';
import { MRT_SortingState } from 'material-react-table';
import { isEmpty } from 'lodash';

import {
  formatDate,
  formatAsLocalDateString,
  formatDateToYYMMDDFormat,
  formatTimeTo12Hr,
  getStoredCustomerDetails,
  hasEventId,
  VendorAssignmentAction,
  VendorType,
} from '../../../../Shared/Utilities/utils';
import {
  CurrencySymbol,
  DebounceTime,
  filterConfigs,
  FiltersOfAllJobs,
  FormFieldType,
  NumberOfRowsOptions,
  Paths,
  timeoutValue,
  SnackbarSeverity,
  states,
  PROCESS_EVENTS,
  allJobsEventIds,
  velocitiOrganizationId,
} from '../../../../Shared/Constants/App';
import { Labels } from '../../../../Shared/Components/Common/GraphContainer/GraphContainer';
import DataGrid from '../../../../Shared/Components/Common/DataGrid/DataGrid';
import { getAllJobsColumns } from './AllJobsColumns';
import PageTitle from '../../../../Shared/Components/Common/PageTitle/PageTitle';
import ModifyPopUp from '../../../Jobs/Components/JobDetails/ModifyPopUp';
import Modal from '../../../Jobs/Utilities/Shared/Components/Modal';
import ReassignVendor from './ModifyVendor/ReassignVendor';
import SnackbarMessage from '../../../../Shared/Components/Common/SnackbarMessage/SnackbarMessage';
import DateSelection from '../../../../Shared/Components/Common/DatePicker/DateSelection';
import { FiltersState, setFilterState } from '../../../../Shared/Store/features/filtersSlice';
import { RootState } from '../../../../Shared/Store/store';

import AllJobsApiService from '../../../Jobs/Services/JobManagementService';
import JobCreateApiService from '../../../Jobs/Services/JobCreateService';
import { DropdownOptions, Source } from '../AllSchedules/AllSchedules.Model';
import { AdhocShiftsPayload, IJobDetails, IVendorDetails, JobManagementType } from './AllJobs.Model';
import { dataStatus, JobType, UpdateWorkOrderInput, WorkOrderStatus } from '../../../../API';

import { StyledPagination } from '../../../../Shared/Components/Common/CommonStyle/Pagination.Style';
import { PaginationStyle, searchFieldStyles } from '../AllSchedules/AllJobs.Style';
import { SearchFieldContainer, SearchInput, CustomerContainer } from '../../../../Shared/Components/Layout/styles';
import { ActionsColumn, Linking } from '../../../../Shared/Components/Common/DataGrid/DataGrid.styles';
import {
  buttonStyles,
  DatePickerContainer,
  HeadingText,
} from '../../../Jobs/Components/JobDetails/OneTimeJobDetails.Style';
import WhiteTooltip from '../../../../Shared/Components/Common/CommonStyle/Tooltip.Style';
import { DropDown, SelectInput } from '../../../../Shared/Components/Common/GraphContainer/GraphContainer.styles';
import { Options } from '../../../../Shared/Components/Common/Dropdown/Dropdown.styles';
import { Icons } from '../../../../Shared/Constants/Icons';
import theme from '../../../../Shared/Themes/theme';
import { NoDataFound } from '../../../../Shared/Components/Common/NoDataFound/NoDataFound';
import ConfirmPopup from '../../../Jobs/Components/JobDetails/ConfirmPopup';
import { JobCompletionFormDetails } from '../../Models/Jobs.Model';
import { JobClosureFormFields } from '../../../Jobs/Models/ConfirmPopUp.model';
import WorkOrdersApiService from '../../../Jobs/Services/WorkOrdersService';
import CreateAdhocShifts from '../../../Jobs/Components/CreateAdhocShifts/CreateAdhocShifts';
import CreateRetrospectiveShift from '../../../Jobs/Components/CreateRetrospectiveShift/CreateRetrospectiveShift';
import FilterDropDownOptions from '../AllSchedules/FilterDropDownOptions';
import CreatePeriodicJob from '../../../Jobs/Components/CreatePeriodicJob/CreatePeriodicJob';
import { IProcessEvents } from '../../../../routes/config';
import { JobVendorAssignmentInputDto, VendorAssignmentDto } from '../../../Jobs/Models/JobManagementDashboard.Model';

export type ManageRemoveVendorInput = {
  workOrderId: string;
  vendorId: string;
  jobId: string;
  fromDate?: string;
  vendorName?: string;
  vendorEmail?: string;
  currentVendorId?: string;
  mdVendorType: VendorType;
};

export type ManageAssignVendorInput = {
  workOrderId?: string;
  vendorId?: string;
  jobId?: string;
  fromDate?: string;
  vendorName?: string;
  vendorEmail?: string;
  currentVendorId?: string;
  mdVendorType: VendorType;
};

const AllJobs = (): JSX.Element => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [jobs, setJobs] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [filterValues, setFilterValues] = useState<any[]>([]);
  const [selectedJobName, setSelectedJobName] = useState<string[]>([]);
  const [selectedJobNumber, setSelectedJobNumber] = useState<string[]>([]);
  const [selectedWorkTicketNumber, setSelectedWorkTicketNumber] = useState<string[]>([]);
  const [selectedLocation, setSelectedLocation] = useState({
    jobAddress: [] as string[],
    stateCode: [] as string[],
    city: [] as string[],
  });
  const [selectedVendor, setSelectedVendor] = useState<string[]>([]);
  const [selectedVendorNumber, setSelectedVendorNumber] = useState<string[]>([]);

  const [selectedCustomer, setSelectedCustomer] = useState<string[]>([]);
  const [selectedVendorAdminName, setSelectedVendorAdminName] = useState<string[]>([]);
  const [selectedVendorAdminEmail, setSelectedVendorAdminEmail] = useState<string[]>([]);
  const [selectedWorkOrderStatus, setSelectedWorkOrderStatus] = useState<string[]>([]);
  const [selectedCreatedOn, setSelectedCreatedOn] = useState<string[]>([]);
  const [isFiltersLoading, setIsFiltersLoading] = useState(false);
  const [sorting, setSorting] = useState<MRT_SortingState>([]);
  const [newSort, setNewSort] = useState<boolean>();
  const [newSortOrder, setNewSortOrder] = useState('');
  const [searchText, setSearchText] = useState('');
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [isValueOpen, setValueOpen] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [isRemoveVendor, setIsRemoveVendor] = useState(false);

  const [selectedDropDownValue, setSelectedDropDownValue] = useState<DropdownOptions | ''>('');

  const [isAssignVendorPopupOpen, setIsAssignVendorPopupOpen] = useState(false);

  const [isRemoveVendorPopupOpen, setIsRemoveVendorPopupOpen] = useState(false);

  const [isDataLoading, setIsDataLoading] = useState(false);
  const [vendors, setVendors] = useState<any[]>([]);
  const [vendorTotalCount, setVendorTotalCount] = useState(0);
  const [vendorCurrentPage, setVendorCurrentPage] = useState(1);
  const { t } = useTranslation(['messages', 'jobManagement', 'vendor', 'tableHeader', 'dashboard']);
  const { userId, customerId } = getStoredCustomerDetails() || {};
  const [removeVendorData, setRemoveVendorData] = useState<ManageRemoveVendorInput[]>([]);

  const [reassignVendorJobData, setReassignVendorJobData] = useState<ManageAssignVendorInput[]>([]);
  const [selectedReassignVendorData, setSelectedReassignVendorData] = useState<ManageAssignVendorInput[]>([]);

  const [removeVendorIconClick, setRemoveVendorIconClick] = useState(false);

  const [reassignVendorClick, setReassignVendorClick] = useState(false);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<AlertColor>('success');

  const [isRemoveVendorLoading, setIsRemoveVendorLoading] = useState(false);

  const [isReassignVendorLoading, setIsReassignVendorLoading] = useState(false);

  const [isConfirmVendorPopup, setIsConfirmVendorPopup] = useState(false);

  const [isRemoveVendorNotAllowed, setIsRemoveVendorNotAllowed] = useState(false);

  const [allSelfPerformJobs, setAllSelfPerformJobs] = useState(false);
  const [popUpText, setPopUpText] = useState<React.ReactNode>();
  const [checkBoxText, setCheckBoxText] = useState<React.ReactNode>();
  const [showCheckBox, setShowCheckBox] = useState(false);
  const [allJobsAreMissingVendors, setAllJobsAreMissingVendors] = useState(false);
  const [unSelectJobIds, setUnSelectJobIds] = useState<string[]>([]);
  const [isChecked, setIsChecked] = useState(false);

  const [removeSelfPerformJobs, setRemoveSelfPerformJobs] = useState(false);

  const [isGeoFenceOpen, setIsGeoFenceOpen] = useState(false);
  const [isVendorPickerMode, setIsVendorPickerMode] = useState(false);
  const vendorPickerResolveRef = useRef<((data?: ManageAssignVendorInput[] | undefined) => void) | null>(null);
  const [showSelectAll, setShowSelectAll] = useState(false);
  const [resetSelectionSignal, setResetSelectionSignal] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);

  const [isCompleteJobPopupOpen, setIsCompleteJobPopupOpen] = useState(false);
  const [isCompleteJobLoading, setIsCompleteJobLoading] = useState<boolean>(false);
  const [selectedJobStartDateForCompletion, setSelectedJobStartDateForCompletion] = useState<string>('');
  const [canShowCannotCompleteJobModal, setCanShowCannotCompleteJobModal] = useState<boolean>(false);
  const [selectedWorkOrderIdForCompletion, setSelectedWorkOrderIdForCompletion] = useState('');

  const [isCreateAdhocShift, setIsCreateAdhocShift] = useState<boolean>(false);
  const [isCreateRetrospectiveShift, setIsCreateRetrospectiveShift] = useState<boolean>(false);
  const [isCreateShiftLoading, setIsCreateShiftLoading] = useState<boolean>(false);
  const [isCreatePeriodicJob, setIsCreatePeriodicJob] = useState<boolean>(false);
  const [isFilterPopUpOpen, setIsFilterPopUpOpen] = useState(false);
  const [filterName, setFilterName] = useState<string>('');

  const dataFetchedRef = useRef(false);
  const styles = searchFieldStyles(searchText);

  const toggleValue = () => {
    setValueOpen(!isValueOpen);
  };

  const navigate = useNavigate();

  const dispatch = useDispatch();

  const filterState = useSelector((state: RootState) => state.filters);

  const didRestoreFilters = useRef(false);

  const { control } = useForm({
    defaultValues: {
      specificDate: new Date().toISOString(),
    },
  });

  const processEvents = localStorage.getItem('jobManagementProcessEvents');
  const jobManagementEvents: IProcessEvents[] = processEvents ? JSON.parse(processEvents) : [];

  const hasAnyEvent = jobManagementEvents?.some((event) => allJobsEventIds?.includes(String(event?.eventId)));
  const canShowActionsForJobs =
    hasEventId(jobManagementEvents, PROCESS_EVENTS.RemoveVendor) ||
    hasEventId(jobManagementEvents, PROCESS_EVENTS.MarkJobAsComplete);

  const specificDate = useWatch({
    control,
    name: 'specificDate',
  });

  const { cancelButton, createJobButton, buttonText } = buttonStyles;

  const handleItemsPerPageChange = (event: SelectChangeEvent<number>) => {
    const newItemsPerPage = Number(event.target.value);
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);

    const { jobAddress: selectedJobAddress, stateCode: selectedState, city: selectedCity } = selectedLocation;

    fetchDataWithDebounce({
      currentPage: 1,
      itemsPerPage: newItemsPerPage,
      newSortOrder,
      newSort,
      searchText,
      selectedJobName,
      selectedJobNumber,
      selectedJobAddress,
      selectedState,
      selectedCity,
      selectedVendor,
      selectedVendorNumber,
      selectedCustomer,
      selectedVendorAdminName,
      selectedVendorAdminEmail,
      selectedWorkOrderStatus,
      selectedCreatedOn,
      selectedWorkTicketNumber,
    });
  };

  const renderCell = useCallback((renderedCellValue: React.ReactNode): string | number => {
    // If renderedCellValue is a string or number, return it. Otherwise, return a fallback '-'
    if (typeof renderedCellValue === 'string' || typeof renderedCellValue === 'number') {
      return renderedCellValue ? renderedCellValue : '-';
    }
    if (renderedCellValue && Array.isArray(renderedCellValue)) {
      return renderedCellValue[0]?.finalQuote || renderedCellValue[0]?.finalQuote === 0
        ? `${CurrencySymbol.Dollar}${Number(renderedCellValue[0]?.finalQuote).toFixed(2)}`
        : '-';
    }
    return '-';
  }, []);

  const fetchData = useCallback(
    async (
      currentPage: number,
      limit: number,
      sortBy?: string,
      sortOrder?: boolean,
      searchText?: string,
      jobName?: string,
      jobNumber?: string,
      jobAddress?: string,
      stateCode?: string,
      city?: string,
      vendorName?: string,
      vendorNumber?: string,
      customerName?: string,
      vendorAdminName?: string,
      vendorAdminEmail?: string,
      workOrderStatus?: string,
      createdOn?: string,
      workTicketNumber?: string,
      refreshCache?: boolean
    ) => {
      setIsLoading(true);
      try {
        const sortOrderString = sortOrder !== undefined ? (sortOrder ? 'desc' : 'asc') : undefined;

        const allJobsResponse = await AllJobsApiService.allJobs(currentPage, limit, userId, {
          vendorId: customerId,
          sortBy,
          sortOrder: sortOrderString,
          searchText,
          jobName,
          jobNumber,
          jobAddress,
          stateCode,
          city,
          vendorName,
          vendorNumber,
          customerName,
          vendorAdminName,
          vendorAdminEmail,
          workOrderStatus,
          createdOn,
          workTicketNumber,
          refreshCache,
        });

        if (allJobsResponse?.data?.data) {
          const transformedData = allJobsResponse?.data?.data;
          setJobs(transformedData);
          setTotalCount(allJobsResponse?.data?.metadata?.totalCount);
        } else {
          console.error(t('messages:failedToFetchJobs'));
        }
        dataFetchedRef.current = true;
      } catch (error) {
        console.error(t('messages:errorFetchingJobs'), error);
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  const fetchFilterValues = useCallback(async () => {
    try {
      setIsFiltersLoading(true);
      const filterValuesResponse = await AllJobsApiService.getFilterValues(userId);
      const validData = filterValuesResponse?.data ? filterValuesResponse?.data : [];
      setFilterValues(validData);
    } catch (error) {
      console.error(t('messages:errorFetchingFilterValues'), error);
    }
    setIsFiltersLoading(false);
  }, []);

  const handleChange = (event: any, value: number): void => {
    setCurrentPage(value);

    const { jobAddress: selectedJobAddress, stateCode: selectedState, city: selectedCity } = selectedLocation;

    fetchDataWithDebounce({
      currentPage: value,
      itemsPerPage,
      newSortOrder,
      newSort,
      searchText,
      selectedJobName,
      selectedJobNumber,
      selectedJobAddress,
      selectedState,
      selectedCity,
      selectedVendor,
      selectedVendorNumber,
      selectedCustomer,
      selectedVendorAdminName,
      selectedVendorAdminEmail,
      selectedWorkOrderStatus,
      selectedCreatedOn,
      selectedWorkTicketNumber,
    });
  };

  const handleSort = (sortingObj: MRT_SortingState) => {
    setSorting(sortingObj);
    try {
      const { jobAddress: selectedJobAddress, stateCode: selectedState, city: selectedCity } = selectedLocation;

      if (sortingObj.length) {
        const { id, desc } = sortingObj[0];
        setNewSortOrder(id);
        setNewSort(desc);
        fetchDataWithDebounce({
          currentPage,
          itemsPerPage,
          newSortOrder: id,
          newSort: desc,
          searchText,
          selectedJobName,
          selectedJobNumber,
          selectedJobAddress,
          selectedState,
          selectedCity,
          selectedVendor,
          selectedVendorNumber,
          selectedCustomer,
          selectedVendorAdminName,
          selectedVendorAdminEmail,
          selectedWorkOrderStatus,
          selectedCreatedOn,
          selectedWorkTicketNumber,
        });
      }
    } catch (error) {
      console.error(t('messages:errorFetchingData'));
    }
  };

  const handleCompletionJobPopup = (): void => {
    setIsCompleteJobPopupOpen(!isCompleteJobPopupOpen);
  };

  const handleOnCompleteJob = async (details: JobCompletionFormDetails): Promise<void> => {
    try {
      setIsCompleteJobLoading(true);
      const payload: UpdateWorkOrderInput = {
        workOrderId: selectedWorkOrderIdForCompletion ?? '',
        status: WorkOrderStatus.Completed,
      };
      if (details[JobClosureFormFields.ClosureDate]) {
        payload.actualEndDate = details[JobClosureFormFields.ClosureDate];
      }
      const updateWorkOrderStatusResponse = await WorkOrdersApiService.updateWorkOrderStatus(payload);
      if (!isEmpty(updateWorkOrderStatusResponse.errors)) {
        setSnackbarMessage(`${t('vendor:errorOccurred')}`);
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      } else if (
        isEmpty(updateWorkOrderStatusResponse?.errors ?? []) &&
        !isEmpty(updateWorkOrderStatusResponse?.data ?? [])
      ) {
        fetchDataWithDebounce({
          currentPage,
          itemsPerPage,
          newSortOrder,
          newSort,
          searchText,
          selectedJobName,
          selectedJobNumber,
          selectedLocation,
          selectedVendor,
          selectedVendorNumber,
          selectedCustomer,
          selectedVendorAdminName,
          selectedVendorAdminEmail,
          selectedWorkOrderStatus,
          selectedCreatedOn,
          refreshCache: true,
        });
        setSnackbarMessage(`${t('assignJob:markedAsComplete')}`);
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        setIsCompleteJobPopupOpen(false);
      }
    } catch (e: any) {
      setSnackbarMessage(e);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setIsCompleteJobLoading(false);
    }
  };

  const handleCloseCannotCompleteJobModal = () => {
    setCanShowCannotCompleteJobModal(false);
  };

  const renderActionsCell = (row: any): JSX.Element => {
    return (
      <ActionsColumn>
        {hasEventId(jobManagementEvents, PROCESS_EVENTS.RemoveVendor) && (
          <Linking
            to="#"
            onClick={() => {
              setRemoveVendorIconClick(true);
              handleOpenRemoveVendorPopup([row.original]);
            }}>
            <IconButton
              size="large"
              sx={{
                '&:hover': {
                  background: theme.palette.primary.main,
                },
              }}>
              <WhiteTooltip title={t('jobManagement:removeVendor')}>
                <img src={Icons.CancelIcon} />
              </WhiteTooltip>
            </IconButton>
          </Linking>
        )}
        {hasEventId(jobManagementEvents, PROCESS_EVENTS.MarkJobAsComplete) && (
          <IconButton
            size="large"
            sx={{
              '&:hover': {
                background: theme.palette.primary.main,
              },
            }}
            onClick={() => {
              const { jobType, parentWorkOrderId, startDate, workTicketNumber, workOrderId } = row?.original ?? {};

              if (jobType === JobType.Recurring || jobType === JobType.Periodic || !isEmpty(workTicketNumber)) {
                setSelectedWorkOrderIdForCompletion(parentWorkOrderId || workOrderId);
                setSelectedJobStartDateForCompletion(startDate as string);
                handleCompletionJobPopup();
              } else {
                setCanShowCannotCompleteJobModal(true);
              }
            }}>
            <WhiteTooltip title={t('homePage:complete')}>
              <img src={Icons.CompleteJobIcon} width={24} height={24} alt={t('altTexts:complete')} />
            </WhiteTooltip>
          </IconButton>
        )}
      </ActionsColumn>
    );
  };
  const filters = Array.isArray(filterConfigs)
    ? // eslint-disable-next-line sonarjs/cognitive-complexity
      filterConfigs.map(({ title, name, key, format }) => {
        return {
          title,
          type: FormFieldType.DropDown,
          name,
          options: Array.from(
            new Map(
              Array.isArray(filterValues)
                ? filterValues
                    .filter((job) => {
                      const actualKey =
                        name === FiltersOfAllJobs.JobNumber
                          ? job.jobType === JobType.Recurring
                            ? 'intId'
                            : 'intReferenceId'
                          : key;
                      return job?.[actualKey];
                    })
                    .map((job) => {
                      const actualKey =
                        name === FiltersOfAllJobs.JobNumber
                          ? job.jobType === JobType.Recurring
                            ? 'intId'
                            : 'intReferenceId'
                          : key;
                      const value = job?.[actualKey];
                      let formattedLabel = job?.[actualKey];
                      if (format === 'date') {
                        formattedLabel = formatDate(job?.[actualKey]);
                      } else if (format === 'time') {
                        formattedLabel = formatTimeTo12Hr(job?.[actualKey]);
                      } else if (states[value as keyof typeof states]) {
                        formattedLabel = states[value as keyof typeof states];
                      } else {
                        return [value, { label: formattedLabel, value }];
                      }

                      return [value, { label: formattedLabel, value }];
                    })
                : []
            ).values() || []
          ),
        };
      })
    : [];
  const fetchDataWithDebounce = debounce((params) => {
    fetchData(
      params.currentPage,
      params.itemsPerPage,
      params.newSortOrder,
      params.newSort,
      params.searchText,
      params.selectedJobName,
      params.selectedJobNumber,
      params.selectedJobAddress,
      params.selectedState,
      params.selectedCity,
      params.selectedVendor,
      params.selectedVendorNumber,
      params.selectedCustomer,
      params.selectedVendorAdminName,
      params.selectedVendorAdminEmail,
      params.selectedWorkOrderStatus,
      params.selectedCreatedOn,
      params.selectedWorkTicketNumber,
      params.refreshCache
    );
  }, DebounceTime);

  const handleSearchTextChange = (text: React.SetStateAction<string>) => {
    setResetSelectionSignal((prev) => prev + 1);

    setSearchText(text);

    const { jobAddress: selectedJobAddress, stateCode: selectedState, city: selectedCity } = selectedLocation;
    fetchDataWithDebounce({
      currentPage,
      itemsPerPage,
      newSortOrder,
      newSort,
      searchText: text,
      selectedJobName,
      selectedJobNumber,
      selectedJobAddress,
      selectedState,
      selectedCity,
      selectedVendor,
      selectedVendorNumber,
      selectedCustomer,
      selectedVendorAdminName,
      selectedVendorAdminEmail,
      selectedWorkOrderStatus,
      selectedCreatedOn,
      selectedWorkTicketNumber,
    });
  };

  // eslint-disable-next-line sonarjs/cognitive-complexity
  const handleFilterChange = (filterType: string, filterValue: string[]) => {
    const value = filterValue;
    switch (filterType) {
      case 'jobName':
        setSelectedJobName(filterValue);
        break;
      case 'jobAddress':
        setSelectedLocation((prev) => ({ ...prev, jobAddress: value }));
        break;
      case 'stateCode':
        setSelectedLocation((prev) => ({ ...prev, stateCode: value }));
        break;
      case 'city':
        setSelectedLocation((prev) => ({ ...prev, city: value }));
        break;
      case 'vendor':
        setSelectedVendor(filterValue);
        break;
      case 'vendorNumber':
        setSelectedVendorNumber(filterValue);
        break;
      case 'customer':
        setSelectedCustomer(filterValue);
        break;
      case 'jobNumber':
        setSelectedJobNumber(filterValue);
        break;
      case 'workOrderStatus':
        setSelectedWorkOrderStatus(filterValue);
        break;
      case 'vendorAdminName':
        setSelectedVendorAdminName(filterValue);
        break;
      case 'vendorAdminEmail':
        setSelectedVendorAdminEmail(filterValue);
        break;
      case 'createdOn':
        setSelectedCreatedOn(filterValue);
        break;
      case 'workTicketNumber':
        setSelectedWorkTicketNumber(filterValue);
        break;
      default:
        break;
    }

    fetchDataWithDebounce({
      currentPage,
      itemsPerPage,
      newSortOrder,
      newSort,
      searchText,
      selectedJobName: filterType === 'jobName' ? filterValue : selectedJobName,
      selectedJobNumber: filterType === 'jobNumber' ? filterValue : selectedJobNumber,
      selectedJobAddress: filterType === 'jobAddress' ? filterValue : selectedLocation.jobAddress,
      selectedState: filterType === 'stateCode' ? filterValue : selectedLocation.stateCode,
      selectedCity: filterType === 'city' ? filterValue : selectedLocation.city,
      selectedVendor: filterType === 'vendor' ? filterValue : selectedVendor,
      selectedVendorNumber: filterType === 'vendorNumber' ? filterValue : selectedVendorNumber,
      selectedCustomer: filterType === 'customer' ? filterValue : selectedCustomer,
      selectedVendorAdminName: filterType === 'vendorAdminName' ? filterValue : selectedVendorAdminName,
      selectedVendorAdminEmail: filterType === 'vendorAdminEmail' ? filterValue : selectedVendorAdminEmail,
      selectedWorkOrderStatus: filterType === 'workOrderStatus' ? filterValue : selectedWorkOrderStatus,
      selectedCreatedOn: filterType === 'createdOn' ? filterValue : selectedCreatedOn,
      selectedWorkTicketNumber: filterType === 'workTicketNumber' ? filterValue : selectedWorkTicketNumber,
    });
    handleCloseFilterPopUp();
  };

  const handleClickVendor = (cell: any) => {
    const data = cell?.row?.original;
    handleOpenReassignVendorPopup([data]);
  };

  const columns = useMemo(
    () =>
      getAllJobsColumns(
        renderCell,
        renderActionsCell,
        t,
        filters,
        handleFilterChange,
        isFiltersLoading,
        selectedJobNumber,
        selectedJobName,
        selectedLocation.jobAddress,
        selectedLocation.stateCode,
        selectedLocation.city,
        selectedVendor,
        selectedVendorNumber,
        selectedCustomer,
        selectedVendorAdminName,
        selectedVendorAdminEmail,
        selectedWorkOrderStatus,
        selectedCreatedOn,
        selectedWorkTicketNumber,
        handleClickVendor,
        setIsFilterPopUpOpen,
        setFilterName,
        jobManagementEvents
      ),
    [jobs, renderCell, renderActionsCell, fetchData]
  );

  useEffect(() => {
    if (selectedDropDownValue === DropdownOptions.RemoveVendor) {
      handleOpenRemoveVendorPopup(selectedRows);
    } else if (selectedDropDownValue === DropdownOptions.ReassignVendor) {
      handleOpenReassignVendorPopup(selectedRows);
    } else if (selectedDropDownValue === DropdownOptions.ModifyGeoFence) {
      if (selectedRows?.length > 1) {
        setIsGeoFenceOpen(true);
      } else {
        handleGeoFence();
      }
    } else if (selectedDropDownValue === DropdownOptions.CreateAdhocShifts) {
      setIsCreateAdhocShift(true);
    } else if (selectedDropDownValue === DropdownOptions.CreateRetrospectiveShift) {
      setIsCreateRetrospectiveShift(true);
    } else if (selectedDropDownValue === DropdownOptions.CreatePeriodicJob) {
      setIsCreatePeriodicJob(true);
    }
  }, [selectedDropDownValue]);

  // eslint-disable-next-line sonarjs/cognitive-complexity
  const handleOpenRemoveVendorPopup = (data: any) => {
    const filteredSelfPerformJobs = data.filter((data: IJobDetails) => isEmpty(data.parentWorkOrderId));
    const filteredSubContractorJobs = data.filter((data: IJobDetails) => !isEmpty(data.parentWorkOrderId));
    const isSelectedSelfPerformJob = filteredSelfPerformJobs?.length > 0;
    const allSelfPerformJobs = data.every((job: IJobDetails) => isEmpty(job.parentWorkOrderId));

    const jobsWithOutVendor = data.filter((job: IVendorDetails) => !job.vendorId && !job.vendorName);
    const jobsWithVendor = data.filter((job: IVendorDetails) => job.vendorId && job.vendorName);
    const isSelectedJobWithOutVendor = jobsWithOutVendor?.length > 0;
    const AllJobsWithOutVendor = data.every((job: IVendorDetails) => isEmpty(job.vendorId) && isEmpty(job.vendorName));

    if (isSelectedSelfPerformJob) {
      setIsRemoveVendorNotAllowed(true);

      const selfPerformJobNames = filteredSelfPerformJobs.map((job: IJobDetails) => job.jobNumber).join(', ');
      const selfPerformJobIds = filteredSelfPerformJobs.map((job: IJobDetails) => job.jobId);

      if (isSelectedJobWithOutVendor) {
        const jobsWithOutVendorNames = jobsWithOutVendor.map((job: IJobDetails) => job.jobNumber).join(', ');
        const jobsWithOutVendorIds = jobsWithOutVendor.map((job: IJobDetails) => job.jobId);
        const filteredJobsWithVendors = jobsWithVendor.filter((data: IJobDetails) => !isEmpty(data.parentWorkOrderId));

        setAllJobsAreMissingVendors(AllJobsWithOutVendor);

        setAllSelfPerformJobs(allSelfPerformJobs);
        setUnSelectJobIds([...selfPerformJobIds, ...jobsWithOutVendorIds]);

        setPopUpText(
          <>
            {selectedRows?.length > 1 ? (
              <>
                {t('jobManagement:followingJobsAre')}
                <strong>{t('jobManagement:SelfPerform')}</strong> {t('jobManagement:cannotRemoveVendor')}
                <br />
                <strong>{selfPerformJobNames}</strong>
                <br />
                <br />
                {t('jobManagement:followingJobs')} <strong>{`${t('jobManagement:noVendorsAssigned')}:`}</strong>
                <br />
                <strong>{jobsWithOutVendorNames}</strong>
                <br />
                <br />
                {t('jobManagement:unSelectMultipleToProceed')}
              </>
            ) : (
              <>
                <strong>{selfPerformJobNames}</strong> {t('jobManagement:isA')}
                <strong>{t('jobManagement:SelfPerformJob')}</strong>
                <br />
                <br />
                <strong>{jobsWithOutVendorNames}</strong> {t('jobManagement:isAJob')}
                <strong>{t('jobManagement:noVendorsAssigned')}</strong>
                <br />
                <br />
                {t('jobManagement:unSelectToProceed')}
              </>
            )}
          </>
        );
        setCheckBoxText(t('jobManagement:ExcludeJobsWithMissingVendorsAndSelfPerform'));

        setShowCheckBox(!isEmpty(filteredJobsWithVendors));

        const vendorDataArray = Array.isArray(filteredJobsWithVendors)
          ? filteredJobsWithVendors.map((item) => ({
              workOrderId: item?.workOrderId,
              vendorId: item?.vendorId,
              jobId: item?.jobId,
              jobName: item?.jobName,
              vendorName: item?.vendorName,
              mdVendorType: item?.vendorId === velocitiOrganizationId ? VendorType.SELF : VendorType.SUBCONTRACT,
            }))
          : [
              {
                workOrderId: filteredJobsWithVendors?.workOrderId,
                vendorId: filteredJobsWithVendors?.vendorId,
                jobId: filteredJobsWithVendors?.jobId,
                jobName: filteredJobsWithVendors?.jobName,
                vendorName: filteredJobsWithVendors?.vendorName,
                mdVendorType:
                  filteredJobsWithVendors?.vendorId === velocitiOrganizationId
                    ? VendorType.SELF
                    : VendorType.SUBCONTRACT,
              },
            ];

        setRemoveVendorData(vendorDataArray);
      } else {
        setAllSelfPerformJobs(allSelfPerformJobs);
        setUnSelectJobIds(selfPerformJobIds);
        setPopUpText(
          <>
            <strong>{selfPerformJobNames}</strong>
            {selfPerformJobIds?.length > 1
              ? t('jobManagement:unSelectSelfPerformJobs')
              : t('jobManagement:unSelectSelfPerformJob')}
            <br />
            {selfPerformJobIds?.length > 1
              ? t('jobManagement:unSelectMultipleToProceed')
              : t('jobManagement:unSelectToProceed')}
          </>
        );
        setCheckBoxText(t('jobManagement:ExcludeSelfPerformJobs'));
        setShowCheckBox(!allSelfPerformJobs);

        const vendorDataArray = Array.isArray(filteredSubContractorJobs)
          ? filteredSubContractorJobs.map((item) => ({
              workOrderId: item?.workOrderId,
              vendorId: item?.vendorId,
              jobId: item?.jobId,
              jobName: item?.jobName,
              vendorName: item?.vendorName,
              mdVendorType: item?.vendorId === velocitiOrganizationId ? VendorType.SELF : VendorType.SUBCONTRACT,
            }))
          : [
              {
                workOrderId: filteredSubContractorJobs?.workOrderId,
                vendorId: filteredSubContractorJobs?.vendorId,
                jobId: filteredSubContractorJobs?.jobId,
                jobName: filteredSubContractorJobs?.jobName,
                vendorName: filteredSubContractorJobs?.vendorName,
                mdVendorType:
                  filteredSubContractorJobs?.vendorId === velocitiOrganizationId
                    ? VendorType.SELF
                    : VendorType.SUBCONTRACT,
              },
            ];

        setRemoveVendorData(vendorDataArray);
      }
    } else {
      if (isSelectedJobWithOutVendor) {
        setIsRemoveVendorNotAllowed(true);
        const jobsWithOutVendorNames = jobsWithOutVendor.map((job: IJobDetails) => job.jobNumber).join(', ');

        const jobsWithOutVendorIds = jobsWithOutVendor.map((job: IJobDetails) => job.jobId);
        setAllJobsAreMissingVendors(AllJobsWithOutVendor);
        setUnSelectJobIds(jobsWithOutVendorIds);
        setPopUpText(
          <>
            <strong>{jobsWithOutVendorNames}</strong>
            {jobsWithOutVendorIds?.length > 1
              ? t('jobManagement:jobsWithMissingVendors')
              : t('jobManagement:jobWithMissingVendor')}
            <br />
            {jobsWithOutVendorIds?.length > 1
              ? t('jobManagement:unSelectMultipleToProceed')
              : t('jobManagement:unSelectToProceed')}
          </>
        );
        setCheckBoxText(t('jobManagement:ExcludeJobsWithMissingVendors'));
        setShowCheckBox(!AllJobsWithOutVendor);

        const vendorDataArray = Array.isArray(jobsWithVendor)
          ? jobsWithVendor.map((item) => ({
              workOrderId: item?.workOrderId,
              vendorId: item?.vendorId,
              jobId: item?.jobId,
              jobName: item?.jobName,
              vendorName: item?.vendorName,
              mdVendorType: item?.vendorId === velocitiOrganizationId ? VendorType.SELF : VendorType.SUBCONTRACT,
            }))
          : [
              {
                workOrderId: jobsWithVendor?.workOrderId,
                vendorId: jobsWithVendor?.vendorId,
                jobId: jobsWithVendor?.jobId,
                jobName: jobsWithVendor?.jobName,
                vendorName: jobsWithVendor?.vendorName,
                mdVendorType:
                  jobsWithVendor?.vendorId === velocitiOrganizationId ? VendorType.SELF : VendorType.SUBCONTRACT,
              },
            ];

        setRemoveVendorData(vendorDataArray);
      } else {
        const vendorDataArray = Array.isArray(data)
          ? data.map((item) => ({
              workOrderId: item?.workOrderId,
              vendorId: item?.vendorId,
              jobId: item?.jobId,
              jobName: item?.jobName,
              vendorName: item?.vendorName,
              mdVendorType: item?.vendorId === velocitiOrganizationId ? VendorType.SELF : VendorType.SUBCONTRACT,
            }))
          : [
              {
                workOrderId: data?.workOrderId,
                vendorId: data?.vendorId,
                jobId: data?.jobId,
                jobName: data?.jobName,
                vendorName: data?.vendorName,
                mdVendorType: data?.vendorId === velocitiOrganizationId ? VendorType.SELF : VendorType.SUBCONTRACT,
              },
            ];

        setRemoveVendorData(vendorDataArray);
        setIsRemoveVendorPopupOpen(true);
      }
    }
  };

  const handleCloseRemoveVendorPopup = () => {
    setIsRemoveVendor(false);
    setIsRemoveVendorPopupOpen(false);
    setRemoveSelfPerformJobs(false);
    setSelectedDropDownValue('');
    setIsChecked(false);
  };

  const handleCloseRemoveVendorNotAllowedPopup = () => {
    setIsRemoveVendorNotAllowed(false);
    setSelectedDropDownValue('');
  };

  const handleContinueRemoveVendorPopup = () => {
    setIsRemoveVendorNotAllowed(false);
    if (isChecked) {
      if (selectedDropDownValue === DropdownOptions.RemoveVendor) {
        setIsRemoveVendorPopupOpen(true);
        setRemoveSelfPerformJobs(true);
      } else if (selectedDropDownValue === DropdownOptions.ReassignVendor) {
        setIsAssignVendorPopupOpen(true);
        setReassignVendorClick(true);
        setRemoveSelfPerformJobs(true);
      }
    } else {
      setSelectedDropDownValue('');
    }
  };

  // eslint-disable-next-line sonarjs/cognitive-complexity
  const handleOpenReassignVendorPopup = (data: any) => {
    setIsAssignVendorPopupOpen(true);
    setReassignVendorClick(true);
    const vendorDataArray = Array.isArray(data)
      ? data?.map((item) => ({
          workOrderId: item?.workOrderId || '',
          jobId: item?.jobId || '',
          jobName: item?.jobName || '',
          currentVendorId: item?.vendorId || '',
          mdVendorType: item?.vendorId === velocitiOrganizationId ? VendorType.SELF : VendorType.SUBCONTRACT,
        }))
      : [
          {
            workOrderId: data?.workOrderId || '',
            jobId: data?.jobId || '',
            jobName: data?.jobName || '',
            currentVendorId: data?.vendorId || '',
            mdVendorType: data?.vendorId === velocitiOrganizationId ? VendorType.SELF : VendorType.SUBCONTRACT,
          },
        ];
    setReassignVendorJobData(vendorDataArray);
    getAllVendors(1, itemsPerPage);
    setVendorCurrentPage(1);
  };

  const handleVendorChange = useCallback(
    (pickerMode: boolean = false) => {
      setIsAssignVendorPopupOpen(true);
      getAllVendors(1, itemsPerPage);
      setIsVendorPickerMode(pickerMode);

      if (!pickerMode) return Promise.resolve(undefined);

      return new Promise<ManageAssignVendorInput[] | undefined>((resolve) => {
        vendorPickerResolveRef.current = resolve;
      });
    },
    [itemsPerPage]
  );
  const handleCloseReassignVendorPopup = () => {
    setIsAssignVendorPopupOpen(false);
    setSelectedDropDownValue('');
    setRemoveSelfPerformJobs(false);
    setIsChecked(false);
    if (isVendorPickerMode && vendorPickerResolveRef.current) {
      vendorPickerResolveRef.current(selectedReassignVendorData?.length ? selectedReassignVendorData : undefined);
      vendorPickerResolveRef.current = null;
      setIsVendorPickerMode(false);
    }
  };

  const handleCloseRemoveVendor = () => {
    setIsRemoveVendorPopupOpen(false);
    setSelectedDropDownValue('');
    setRemoveSelfPerformJobs(false);
  };

  const showSnackbar = (severity: AlertColor, message: string) => {
    setSnackbarOpen(true);
    setSnackbarSeverity(severity);
    setSnackbarMessage(message);
  };

  // eslint-disable-next-line @typescript-eslint/require-await
  const handleRemoveVendor = async () => {
    setIsRemoveVendorLoading(true);

    const formattedDate = formatDateToYYMMDDFormat(specificDate);

    const jobAssignments: JobVendorAssignmentInputDto[] =
      selectedDropDownValue === DropdownOptions.RemoveVendor || removeVendorIconClick
        ? removeVendorData.map((jobData) => ({
            workOrderId: jobData.workOrderId ?? '',
            jobId: jobData.jobId ?? '',
            vendorId: jobData?.vendorId ?? '',
            vendorName: jobData?.vendorName ?? '',
            vendorEmail: jobData?.vendorEmail ?? '',
          }))
        : [];

    const vendorAssignmentDto: VendorAssignmentDto = {
      existingVendorType: removeVendorData?.[0]?.mdVendorType,
      updateVendorType: VendorType.NONE,
      action: VendorAssignmentAction.UPDATE,
      jobAssignments,
      effectiveStartDate: formattedDate || '',
    };

    const removeVendorAssignments = await AllJobsApiService.manageVendorAssignments(vendorAssignmentDto, userId);

    if (!isEmpty(removeVendorAssignments?.data)) {
      if (
        !isEmpty(removeVendorAssignments?.data?.jobAssignmentsSuccess) &&
        isEmpty(removeVendorAssignments?.data?.jobAssignmentsFailure)
      ) {
        showSnackbar(SnackbarSeverity.success, t('jobManagement:vendorRemovalSuccessMessage'));
      } else if (
        isEmpty(removeVendorAssignments?.data?.jobAssignmentsSuccess) &&
        !isEmpty(removeVendorAssignments?.data?.jobAssignmentsFailure)
      ) {
        showSnackbar(SnackbarSeverity.error, t('jobManagement:vendorRemovalErrorMessage'));
      } else {
        showSnackbar(SnackbarSeverity.error, t('jobManagement:someThingWentWrong'));
      }
    } else {
      showSnackbar(SnackbarSeverity.error, t('jobManagement:vendorRemovalErrorMessage'));
    }
    setIsRemoveVendorLoading(false);
    setIsRemoveVendor(false);
    setIsRemoveVendorPopupOpen(false);
    setSelectedDropDownValue('');
    if (
      !isEmpty(removeVendorAssignments?.data?.jobAssignmentsSuccess) &&
      isEmpty(removeVendorAssignments?.data?.jobAssignmentsFailure)
    ) {
      fetchDataWithDebounce({
        currentPage,
        itemsPerPage,
        newSortOrder,
        newSort,
        searchText,
        selectedJobName,
        selectedJobNumber,
        selectedLocation,
        selectedVendor,
        selectedVendorNumber,
        selectedCustomer,
        selectedVendorAdminName,
        selectedVendorAdminEmail,
        selectedWorkOrderStatus,
        selectedCreatedOn,
        refreshCache: true,
      });
    }
    setRemoveSelfPerformJobs(false);
  };

  const renderReassignVendorHeaderContent = () => {
    return (
      <HeadingText sx={{ fontSize: theme.typography.h2?.fontSize }}>{t('jobManagement:reassignVendor')}</HeadingText>
    );
  };

  const renderRemoveVendorHeaderContent = () => {
    return (
      <HeadingText sx={{ fontSize: theme.typography.h2?.fontSize }}>{t('jobManagement:removeVendor')}</HeadingText>
    );
  };

  const handleOpenConfirmPopup = () => {
    setIsConfirmVendorPopup(true);
  };

  const handleCloseConfirmPopup = () => {
    setIsConfirmVendorPopup(false);
  };

  // eslint-disable-next-line sonarjs/cognitive-complexity
  const handleVendorConfirmButton = async () => {
    setIsReassignVendorLoading(true);

    const selectedNewVendor = selectedReassignVendorData?.[0];

    const formattedDate = formatDateToYYMMDDFormat(selectedNewVendor.fromDate as string);

    const jobAssignments: JobVendorAssignmentInputDto[] =
      selectedDropDownValue === DropdownOptions.ReassignVendor || reassignVendorClick
        ? reassignVendorJobData.map((jobData) => ({
            workOrderId: jobData.workOrderId ?? '',
            jobId: jobData.jobId ?? '',
            vendorId: selectedNewVendor?.vendorId ?? '',
            vendorName: selectedNewVendor?.vendorName ?? '',
            vendorEmail: selectedNewVendor?.vendorEmail ?? '',
          }))
        : [];

    const vendorAssignmentDto: VendorAssignmentDto = {
      existingVendorType: reassignVendorJobData?.[0]?.mdVendorType,
      updateVendorType: selectedNewVendor?.mdVendorType,
      action: VendorAssignmentAction.UPDATE,
      jobAssignments,
      effectiveStartDate: formattedDate || '',
    };

    const reassignVendorAssignments = await AllJobsApiService.manageVendorAssignments(vendorAssignmentDto, userId);

    if (!isEmpty(reassignVendorAssignments?.data)) {
      if (
        !isEmpty(reassignVendorAssignments?.data?.jobAssignmentsSuccess) &&
        isEmpty(reassignVendorAssignments?.data?.jobAssignmentsFailure)
      ) {
        showSnackbar(SnackbarSeverity.success, t('jobManagement:vendorReassignmentSuccessMessage'));
      } else if (
        isEmpty(reassignVendorAssignments?.data?.jobAssignmentsSuccess) &&
        !isEmpty(reassignVendorAssignments?.data?.jobAssignmentsFailure)
      ) {
        showSnackbar(SnackbarSeverity.error, t('jobManagement:vendorReassignmentErrorMessage'));
      } else {
        showSnackbar(SnackbarSeverity.error, t('jobManagement:someThingWentWrong'));
      }
    } else {
      showSnackbar(SnackbarSeverity.error, t('jobManagement:vendorReassignmentErrorMessage'));
    }
    setIsReassignVendorLoading(false);
    setIsConfirmVendorPopup(false);
    setIsAssignVendorPopupOpen(false);
    setSelectedDropDownValue('');

    if (
      !isEmpty(reassignVendorAssignments?.data?.jobAssignmentsSuccess) &&
      isEmpty(reassignVendorAssignments?.data?.jobAssignmentsFailure)
    ) {
      fetchDataWithDebounce({
        currentPage,
        itemsPerPage,
        newSortOrder,
        newSort,
        searchText,
        selectedJobName,
        selectedJobNumber,
        selectedLocation,
        selectedVendor,
        selectedVendorNumber,
        selectedCustomer,
        selectedVendorAdminName,
        selectedVendorAdminEmail,
        selectedWorkOrderStatus,
        selectedCreatedOn,
        refreshCache: true,
      });

      const workOrderIds = reassignVendorAssignments?.data?.jobAssignmentsSuccess.map(
        (item: { workOrderId: any }) => item.workOrderId
      );

      dispatch(setFilterState(getCurrentFilters()));
      setTimeout(() => {
        navigate(Paths.ManageAssociatesAndSupervisors, {
          state: {
            workOrderIds,
            isReassigningVendor: true,
            jobsData: {
              ...reassignVendorAssignments?.data?.jobAssignmentsSuccess?.[0]?.jobDetails,
              workOrderId: reassignVendorAssignments?.data?.jobAssignmentsSuccess?.[0]?.workOrderId,
              facilityId:
                reassignVendorAssignments?.data?.jobAssignmentsSuccess?.[0]?.jobDetails?.jobFacilities?.[0]?.facilityId,
              jobEndDate: reassignVendorAssignments?.data?.jobAssignmentsSuccess?.[0]?.jobDetails?.stopDate,
            },
          },
        });
        setRefreshKey((prev) => prev + 1);
      }, timeoutValue);
    }
    setRemoveSelfPerformJobs(false);
  };

  const renderReassignVendorBodyContent = () => {
    return (
      <>
        <ReassignVendor
          key={refreshKey}
          teamMembers={vendors}
          isDataLoading={isDataLoading}
          setTotalCount={setVendorTotalCount}
          setCurrentPage={setVendorCurrentPage}
          totalCount={vendorTotalCount}
          currentPage={vendorCurrentPage}
          setSelectedVendorData={setSelectedReassignVendorData}
          handleVendorConfirmButton={handleOpenConfirmPopup}
          handleCloseReassignVendorPopup={handleCloseReassignVendorPopup}
          fetchVendorsData={debouncedFetchAllVendors}
          reassignVendorJobData={reassignVendorJobData}
          selectedVendor={reassignVendorJobData?.[0]?.currentVendorId}
          selectedRows={selectedRows}
          pickerMode={isVendorPickerMode}
        />
      </>
    );
  };

  const renderRemoveVendorBodyContent = () => {
    return (
      <Box>
        <DatePickerContainer
          sx={{
            marginBottom: '1rem',
          }}
          isDatePickerForTabletScreen={true}>
          <DateSelection
            requiredText={t('calendar:dateFieldIsRequired')}
            control={control}
            fieldName={'specificDate'}
            label={t('jobManagement:effectiveStartDate')}
            isExcludePastDate={true}
            isExcludeFutureDate={false}
            textFieldStyles={{
              '& .MuiOutlinedInput-root': {
                '& fieldset,&:hover fieldset,&.Mui-focused fieldset': {
                  borderWidth: 1,
                  borderColor: theme.palette.primary.dark,
                  borderRadius: '1rem',
                },
              },
              '& .MuiInputLabel-root': {
                color: theme.palette.primary.dark,
                '&.Mui-focused': {
                  color: theme.palette.primary.dark,
                },
              },
            }}
          />
        </DatePickerContainer>
        <Box style={{ display: 'flex', justifyContent: 'center' }}>
          <Button
            disabled={isEmpty(specificDate)}
            sx={{
              ...cancelButton,
              cursor: isEmpty(specificDate) ? 'not-allowed' : 'pointer',
              maxWidth: '350px',
              marginTop: '2em',
              width: '100%',
              opacity: isEmpty(specificDate) ? '0.5' : '1',
            }}
            onClick={handleCloseRemoveVendor}>
            <Typography sx={{ ...buttonText, color: theme.palette.primary.dark }}>{t('assignJob:cancel')}</Typography>
          </Button>
          <Button
            disabled={false}
            sx={{
              ...createJobButton,
              cursor: isEmpty(specificDate) ? 'not-allowed' : 'pointer',
              maxWidth: '350px',
              marginTop: '2em',
              width: '100%',
              opacity: isEmpty(specificDate) ? '0.5' : '1',
            }}
            onClick={() => setIsRemoveVendor(true)}>
            {isReassignVendorLoading ? (
              <CircularProgress size={30} sx={{ color: theme.palette.common.white }} />
            ) : (
              <Typography sx={{ ...buttonText, color: theme.palette.common.white }}>
                {t('assignJob:confirm')}
              </Typography>
            )}
          </Button>
        </Box>
      </Box>
    );
  };
  const getAllVendors = async (pageNumber?: number, limit?: number, searchText?: string) => {
    setIsDataLoading(true);
    const searchedVendorsResponse = await JobCreateApiService.allVendorsOnARKPagination(
      '',
      dataStatus.A,
      searchText,
      limit,
      pageNumber,
      userId
    );
    setVendors(searchedVendorsResponse?.data);
    setVendorTotalCount(searchedVendorsResponse?.metaData?.totalCount);
    setIsDataLoading(false);
  };

  const debouncedFetchAllVendors = useCallback(
    debounce((currentPage, limit, searchString) => {
      getAllVendors(currentPage, limit, searchString);
    }, DebounceTime),
    []
  );

  const handleCloseGeoFence = () => {
    setSelectedDropDownValue('');
    setIsGeoFenceOpen(false);
  };

  const getCurrentFilters = () => ({
    jobNumber: selectedJobNumber,
    jobName: selectedJobName,
    jobAddress: selectedLocation?.jobAddress,
    customer: selectedCustomer,
  });

  const handleGeoFence = () => {
    dispatch(setFilterState(getCurrentFilters()));
    navigate('/all-jobs/modify-geofence', {
      state: {
        selectedRows: selectedRows,
      },
    });
  };

  const restoreFilters = (filters: FiltersState) => {
    setSelectedJobName(filters?.jobName || '');
    setSelectedLocation({
      jobAddress: filters?.jobAddress || '',
      stateCode: [],
      city: [],
    });
    setSelectedCustomer(filters?.customer || '');
    setSelectedJobNumber(filters?.jobNumber || '');

    fetchDataWithDebounce({
      currentPage,
      itemsPerPage,
      newSortOrder,
      newSort,
      searchText,
      selectedJobName: filters?.jobName,
      selectedJobNumber: filters?.jobNumber,
      selectedJobAddress: filters?.jobAddress,
      selectedCustomer: filters?.customer,
      refreshCache: true,
    });
  };

  const handleSelectAllData = async () => {
    try {
      const allJobsResponse = await AllJobsApiService.allJobs(currentPage, totalCount, userId, {
        vendorId: customerId,
        sortBy: newSortOrder,
        sortOrder: newSort,
        searchText,
        jobName: selectedJobName,
        jobNumber: selectedJobNumber,
        jobAddress: selectedLocation?.jobAddress,
        vendorName: selectedVendor,
        vendorNumber: selectedVendorNumber,
        customerName: selectedCustomer,
        vendorAdminName: selectedVendorAdminName,
        vendorAdminEmail: selectedVendorAdminEmail,
        workOrderStatus: selectedWorkOrderStatus,
        createdOn: selectedCreatedOn,
        refreshCache: true,
      });

      if (allJobsResponse?.data?.data) {
        return allJobsResponse?.data?.data;
      } else {
        console.error(t('messages:failedToFetchJobs'));
        return [];
      }
    } catch (error) {
      console.error(t('jobManagement:failedToSelectAllRows'), error);
      return [];
    }
  };

  const createAdhocShifts = useCallback(async (details: AdhocShiftsPayload) => {
    try {
      setIsCreateShiftLoading(true);
      details.source = Source.Peazy;
      const { userId } = getStoredCustomerDetails() || {};

      const date = new Date(details?.scheduleStartDate ?? '');

      date?.setDate(date?.getDate() + Number(details?.daysExtendedBy));
      const scheduleEndDate = date?.toISOString()?.split('T')[0];
      details.scheduleEndDate = scheduleEndDate;

      const response = await AllJobsApiService.createAdhocShifts(details, userId);
      if (!isEmpty(response?.data) && isEmpty(response?.errors)) {
        showSnackbar(SnackbarSeverity.success, t('jobManagement:unScheduledWorkSuccessMessage'));
      } else {
        showSnackbar(SnackbarSeverity.error, t('jobManagement:unScheduledWorkErrorMessage'));
      }
    } catch (error) {
      console.log('error', error);
    } finally {
      setIsCreateShiftLoading(false);
      setIsCreateAdhocShift(false);
      setSelectedDropDownValue('');
    }
  }, []);

  const closeAdhocShifts = () => {
    setIsCreateAdhocShift(false);
    setSelectedDropDownValue('');
  };

  const createRetrospectiveShift = useCallback(async (details: AdhocShiftsPayload) => {
    try {
      setIsCreateShiftLoading(true);
      details.source = Source.Peazy;
      const { userId } = getStoredCustomerDetails() || {};

      const startDate = new Date(details?.scheduleStartDate ?? '');
      const endDate = new Date(startDate);
      endDate.setDate(endDate.getDate() + Number(details?.daysExtendedBy ?? 0));
      details.scheduleEndDate = endDate?.toISOString()?.split('T')[0];

      const shiftStartDatetime = new Date(`${formatAsLocalDateString(startDate)}T${details?.shiftStartTime}:00`);
      const shiftEndDatetime = new Date(`${formatAsLocalDateString(endDate)}T${details?.shiftEndTime}:00`);
      details.shiftStartTime = `${String(shiftStartDatetime.getUTCHours()).padStart(2, '0')}:${String(shiftStartDatetime.getUTCMinutes()).padStart(2, '0')}`;
      details.shiftEndTime = `${String(shiftEndDatetime.getUTCHours()).padStart(2, '0')}:${String(shiftEndDatetime.getUTCMinutes()).padStart(2, '0')}`;

      const response = await AllJobsApiService.createRetrospectiveShift(details, userId);
      if (!isEmpty(response?.data) && isEmpty(response?.errors)) {
        showSnackbar(SnackbarSeverity.success, t('jobManagement:retrospectiveShiftSuccessMessage'));
      } else {
        showSnackbar(SnackbarSeverity.error, t('jobManagement:retrospectiveShiftErrorMessage'));
      }
    } catch (error) {
      console.log('error', error);
    } finally {
      setIsCreateShiftLoading(false);
      setIsCreateRetrospectiveShift(false);
      setSelectedDropDownValue('');
    }
  }, []);

  const closeRetrospectiveShift = () => {
    setIsCreateRetrospectiveShift(false);
    setSelectedDropDownValue('');
  };

  const closePeriodicJob = () => {
    setIsCreatePeriodicJob(false);
    setSelectedDropDownValue('');
  };
  useEffect(() => {
    const hasAnyFilter =
      !isEmpty(searchText) ||
      !isEmpty(selectedJobName) ||
      !isEmpty(selectedJobNumber) ||
      !isEmpty(selectedLocation?.jobAddress) ||
      !isEmpty(selectedCustomer);
    if (hasAnyFilter) {
      setShowSelectAll(true);
    } else {
      setShowSelectAll(false);
    }
  }, [
    searchText,
    selectedJobName,
    selectedJobNumber,
    selectedLocation,
    selectedVendor,
    selectedVendorNumber,
    selectedCustomer,
    selectedVendorAdminName,
    selectedVendorAdminEmail,
    selectedWorkOrderStatus,
    selectedCreatedOn,
  ]);

  useEffect(() => {
    const hasFilter =
      filterState?.jobName || filterState?.jobNumber || filterState?.jobAddress || filterState?.customer;
    if (hasFilter) {
      didRestoreFilters.current = true;
      restoreFilters(filterState);
    }
  }, []);

  useEffect(() => {
    fetchFilterValues();
    if (didRestoreFilters.current) {
      didRestoreFilters.current = false;
      return;
    }
    fetchDataWithDebounce({
      currentPage,
      itemsPerPage,
      newSortOrder,
      newSort,
      searchText,
      selectedJobName,
      selectedJobNumber,
      selectedLocation,
      selectedVendor,
      selectedVendorNumber,
      selectedCustomer,
      selectedVendorAdminName,
      selectedVendorAdminEmail,
      selectedWorkOrderStatus,
      selectedCreatedOn,
      refreshCache: true,
    });
  }, []);

  useEffect(() => {
    localStorage.removeItem('woScheduleMasterIds');
    localStorage.removeItem('workOrderIds');
  }, []);

  const handleCloseFilterPopUp = () => {
    setIsFilterPopUpOpen(false);
  };

  const fetchSelectedFilter = () => {
    const { jobAddress: selectedJobAddress } = selectedLocation;

    switch (filterName) {
      case FiltersOfAllJobs.JobName:
        return selectedJobName;
        break;
      case FiltersOfAllJobs.JobAddress:
        return selectedJobAddress;
        break;
      case FiltersOfAllJobs.Customer:
        return selectedCustomer;
        break;
      case FiltersOfAllJobs.JobNumber:
        return selectedJobNumber;
        break;
      default:
        break;
    }
  };

  const renderFilterBodyContent = () => {
    return (
      <>
        <FilterDropDownOptions
          options={filters?.find((filter) => filter?.name === filterName)?.options || []}
          handleFilterChange={(value) => handleFilterChange(filterName, value)}
          filterLoader={isFiltersLoading}
          selectedFilter={fetchSelectedFilter()}
        />
      </>
    );
  };

  return (
    <>
      <PageTitle title={t('vendor:allJobs')} />
      <CustomerContainer>
        {dataFetchedRef.current && !isLoading && !Array.isArray(jobs) ? (
          <NoDataFound />
        ) : (
          <>
            <SearchFieldContainer sx={{ ...styles }}>
              <SearchInput
                placeholder={t('oneTimeJob:search')}
                value={searchText}
                onChange={(e) => handleSearchTextChange(e.target.value)}
              />
              <img src={Icons.SearchIcon} alt={t('altTexts:search')} />
            </SearchFieldContainer>

            <DataGrid
              key={refreshKey}
              columns={columns}
              data={jobs}
              enableRowSelect={true}
              enableColumnPinning={true}
              loader={isLoading}
              errorMessageTitle={t('noData:noDataYet')}
              errorMessageDescription={t('noData:comeBackLater')}
              configureColumns={true}
              sortingObj={sorting}
              sort={handleSort}
              selectActions={hasAnyEvent}
              setSelectedRows={setSelectedRows}
              selectedRows={selectedRows}
              type={JobManagementType.AllJobs}
              setSelectedDropDownValue={setSelectedDropDownValue}
              selectedDropDownValue={selectedDropDownValue}
              totalCount={totalCount}
              isReassigning={true}
              unSelectJobIds={unSelectJobIds}
              removeSelectedJobs={removeSelfPerformJobs}
              onFetchAllRows={handleSelectAllData}
              showSelectAllButton={showSelectAll}
              resetSelectionSignal={resetSelectionSignal}
              storeRowSelection={true}
              processEvents={jobManagementEvents}
              canShowActionsForJobs={canShowActionsForJobs}
              columnFilterDisplayMode={'custom'}
            />
          </>
        )}
        {!isLoading && totalCount > 0 && (
          <PaginationStyle>
            <Stack spacing={2} justifyContent="center" style={{ marginTop: '20px' }}>
              <StyledPagination
                count={Math.ceil(totalCount / itemsPerPage)}
                page={currentPage}
                onChange={(event, value) => {
                  handleChange(event, value);
                }}
                color="primary"
              />
            </Stack>
            <Typography>
              {t('dashboard:totalItems')}: {totalCount}
            </Typography>
            {totalCount > 10 && (
              <Box sx={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                <Typography>{t('dashboard:recordsPerPage')}:</Typography>
                <DropDown>
                  <Box onClick={toggleValue}>
                    <Select
                      value={itemsPerPage}
                      onChange={handleItemsPerPageChange}
                      label="Records"
                      input={<SelectInput />}
                      IconComponent={() => (
                        <img
                          src={Icons.DropdownIcon}
                          style={{
                            transform: isValueOpen ? Labels.Rotate : 'none',
                            position: 'absolute',
                            right: '0.75rem',
                          }}
                        />
                      )}
                      open={isValueOpen}
                      onClose={toggleValue}
                      onOpen={toggleValue}>
                      {NumberOfRowsOptions.map((dataItem) => (
                        <Options key={dataItem.id} value={dataItem.value}>
                          {dataItem.label}
                        </Options>
                      ))}
                    </Select>
                  </Box>
                </DropDown>
              </Box>
            )}
          </PaginationStyle>
        )}
        <ModifyPopUp
          open={isRemoveVendor}
          handleClose={handleCloseRemoveVendorPopup}
          handleOnCloseJob={handleRemoveVendor}
          loading={isRemoveVendorLoading}
          heading={t('jobManagement:removeVendor')}
          text={t('jobManagement:removeVendorPopUp')}
        />
        <ModifyPopUp
          open={isConfirmVendorPopup}
          handleClose={handleCloseConfirmPopup}
          handleOnCloseJob={handleVendorConfirmButton}
          loading={isReassignVendorLoading}
          heading={t('jobManagement:reassignVendor')}
          text={t('jobManagement:reassignVendorPopUp')}
        />
        <Modal
          open={isAssignVendorPopupOpen}
          onClose={handleCloseReassignVendorPopup}
          renderHeader={renderReassignVendorHeaderContent()}
          renderBody={renderReassignVendorBodyContent()}
          styles={{
            '& .MuiPaper-root': { width: '52%' },
          }}
        />
        <Modal
          open={isRemoveVendorPopupOpen}
          onClose={handleCloseRemoveVendor}
          renderHeader={renderRemoveVendorHeaderContent()}
          renderBody={renderRemoveVendorBodyContent()}
          styles={{
            '& .MuiPaper-root': { width: '32rem' },
          }}
        />
        <ModifyPopUp
          open={isRemoveVendorNotAllowed}
          handleClose={handleCloseRemoveVendorNotAllowedPopup}
          handleOnCloseJob={handleContinueRemoveVendorPopup}
          loading={isRemoveVendorLoading}
          heading={''}
          text={popUpText}
          label={allSelfPerformJobs || !isChecked || allJobsAreMissingVendors ? 'Okay' : 'Continue'}
          hideSecondaryLabel={true}
          showCheckBox={showCheckBox}
          isChecked={isChecked}
          setIsChecked={setIsChecked}
          checkBoxText={checkBoxText}
          width={selectedRows?.length > 7 ? '41.25rem' : 'auto'}
        />
        <ModifyPopUp
          open={isGeoFenceOpen}
          handleClose={handleCloseGeoFence}
          handleOnCloseJob={handleGeoFence}
          heading={''}
          text={t('jobManagement:GeoFencePopUpText')}
          label={t('jobManagement:LabelText')}
          width={'41.25rem'}
        />
        <ConfirmPopup
          open={isCompleteJobPopupOpen}
          handleClose={handleCompletionJobPopup}
          handleOnCloseJob={handleOnCompleteJob}
          isCompleteJobLoading={isCompleteJobLoading}
          jobStartDate={selectedJobStartDateForCompletion ?? ''}
        />
        <ModifyPopUp
          open={canShowCannotCompleteJobModal}
          handleClose={handleCloseCannotCompleteJobModal}
          handleOnCloseJob={handleCloseCannotCompleteJobModal}
          heading={t('vendor:cannotCompleteJobModalHeader')}
          text={t('vendor:cannotCompleteJobModalBody')}
          hideSecondaryLabel
          label={t('assignJob:okay')}
        />
        <CreateAdhocShifts
          openShiftCard={isCreateAdhocShift}
          createAdhocShifts={createAdhocShifts}
          closeShiftCard={() => closeAdhocShifts()}
          isLoading={isCreateShiftLoading}
          selectedRows={selectedRows}
        />
        <CreateRetrospectiveShift
          openShiftCard={isCreateRetrospectiveShift}
          createRetrospectiveShift={createRetrospectiveShift}
          closeShiftCard={() => closeRetrospectiveShift()}
          isLoading={isCreateShiftLoading}
          selectedRows={selectedRows}
        />
        <CreatePeriodicJob
          open={isCreatePeriodicJob}
          onClose={() => closePeriodicJob()}
          isLoading={isCreateShiftLoading}
          selectedRows={selectedRows}
          handleVendorChange={handleVendorChange}
        />
        <SnackbarMessage
          open={snackbarOpen}
          successMessage={snackbarMessage}
          errorMessage={snackbarMessage}
          severity={snackbarSeverity}
          onClose={() => setSnackbarOpen(false)}
        />
        <Modal
          open={isFilterPopUpOpen}
          onClose={handleCloseFilterPopUp}
          renderHeader={''}
          renderBody={renderFilterBodyContent()}
          styles={{
            '& .MuiPaper-root': { width: '32rem', padding: '1.5em' },
          }}
          isRemoveDivider={true}
        />
      </CustomerContainer>
    </>
  );
};

export default AllJobs;
